import { BarChart3, Clock3, History, TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { ResultsAttempt, ResultsTopic } from "@/types/results";
import { useTranslation } from "react-i18next";

const RESULT_COLORS = {
  brand: "var(--dp-brand-button)",
  gold: "#a8791c",
} as const;

export function ResultsTrendPill({
  change,
  inverse = false,
  suffix = "%",
}: {
  change: number | null;
  inverse?: boolean;
  suffix?: string;
}) {
  if (change == null) return null;

  const effectiveChange = inverse ? -change : change;
  const positive = effectiveChange >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;

  return (
    <span
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold leading-[18px] tracking-[0.06px]",
        positive
          ? "bg-[var(--dp-brand-soft)] text-[var(--dp-brand-button)]"
          : "bg-[#fdf6e7] text-[#a8791c]",
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {Math.abs(change)}{suffix}
    </span>
  );
}

export function ResultsDonut({
  passingScore,
  score,
}: {
  passingScore: number;
  score: number;
}) {
  const { t } = useTranslation();
  const onTarget = score >= passingScore;
  const color = onTarget ? RESULT_COLORS.brand : RESULT_COLORS.gold;
  const radius = 38;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      aria-label={t("results.percent_correct", { score })}
      className="relative h-[92px] w-[92px] shrink-0"
      role="img"
    >
      <svg aria-hidden="true" className="h-full w-full -rotate-90" viewBox="0 0 92 92">
        <circle cx="46" cy="46" fill="none" r={radius} stroke="#ebecf0" strokeWidth="8" />
        <circle
          cx="46"
          cy="46"
          fill="none"
          r={radius}
          stroke={color}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - Math.min(Math.max(score, 0), 100) / 100)}
          strokeLinecap="round"
          strokeWidth="8"
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-base font-bold"
        style={{ color }}
      >
        {score}%
      </span>
    </div>
  );
}

export function ResultsTopicCard({
  onClick,
  passingScore,
  topic,
}: {
  onClick: () => void;
  passingScore: number;
  topic: ResultsTopic;
}) {
  const { t } = useTranslation();
  const onTarget = topic.score >= passingScore;

  return (
    <button
      className="flex min-h-[164px] w-full flex-col gap-4 rounded-[28px] border border-[#dddfe4] bg-white p-4 text-left outline-none transition-shadow hover:shadow-sm focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
      data-testid="results-topic-card"
      onClick={onClick}
      type="button"
    >
      <span className="flex min-h-12 w-full items-start gap-4">
        <span className="min-w-0 flex-1 text-base font-semibold leading-6 text-[#0a0c11]">
          {topic.title}
        </span>
        <span
          className={cn(
            "inline-flex h-6 shrink-0 items-center rounded-full px-3 text-sm font-medium leading-[21px] tracking-[0.07px]",
            onTarget
              ? "bg-[var(--dp-brand-soft)] text-[var(--dp-brand-button)]"
              : "bg-[#fdf6e7] text-[#a8791c]",
          )}
        >
          {onTarget ? t("results.on_target") : t("results.below_target")}
        </span>
      </span>
      <span className="flex w-full items-center gap-4">
        <ResultsDonut passingScore={passingScore} score={topic.score} />
        <span className="flex min-w-0 flex-1 flex-col items-end gap-2 text-right text-base leading-6 text-[#5b616d]">
          <strong
            className={cn("font-bold", onTarget ? "text-[var(--dp-brand-button)]" : "text-[#a8791c]")}
            data-testid="trend-indicator"
          >
            {topic.improvement == null
              ? "—"
              : `${topic.improvement >= 0 ? "+" : "−"}${Math.abs(topic.improvement)}%`}
          </strong>
          <span>{t("results.correct_summary", { correct: topic.correct, total: topic.total })}</span>
          <span className="font-medium">{t("results.exam_weight", { weight: topic.examWeight })}</span>
        </span>
      </span>
    </button>
  );
}

export function RecentAttemptCard({
  attempt,
  onViewAll,
}: {
  attempt: ResultsAttempt;
  onViewAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="flex min-h-[226px] min-w-0 flex-col justify-between rounded-[28px] border border-[#dddfe4] bg-white p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--dp-brand-soft)] text-[var(--dp-brand-button)]">
            <History aria-hidden="true" className="h-4 w-4" />
          </span>
          <h2 className="text-lg font-semibold leading-[27px] text-[#0a0c11]">{t("results.recent_attempt")}</h2>
        </div>
        <ResultsTrendPill change={attempt.scoreChange} />
      </div>
      <div className="flex items-center gap-4">
        {attempt.image && <img alt="" className="h-12 w-12 shrink-0 object-contain" src={attempt.image} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-end gap-3">
            <strong className="text-[36px] font-semibold leading-[42px] tracking-[-0.18px] text-[#a8791c]">
              {attempt.score}<span className="text-xs leading-[18px]">%</span>
            </strong>
            <span className="pb-1 text-base font-semibold leading-6 text-[#0a0c11]">{attempt.title}</span>
          </div>
          <p className="text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
            {t("results.questions_count", { count: attempt.questionCount })} · {attempt.timeLabel}
          </p>
        </div>
      </div>
      <div className="h-px w-full bg-[#dddfe4]" />
      <button
        className="dp-button-secondary h-12 w-full text-base leading-6 outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
        onClick={onViewAll}
        type="button"
      >
        {t("results.see_previous_attempts")}
      </button>
    </section>
  );
}

export function PreviousAttemptCard({
  attempt,
  onClick,
}: {
  attempt: ResultsAttempt;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const speed = attempt.speedChangeSeconds;
  return (
    <button
      className="flex min-h-[136px] w-full items-center gap-8 rounded-[28px] border border-[#dddfe4] bg-white p-6 text-left outline-none transition-shadow hover:shadow-sm focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
      data-testid="past-exam-card"
      onClick={onClick}
      type="button"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="flex items-center gap-4">
          {attempt.image && <img alt="" className="h-10 w-10 shrink-0 object-contain" src={attempt.image} />}
          <span className="min-w-0">
            <strong className="block truncate text-base font-semibold leading-6 text-[#0a0c11]">{attempt.title}</strong>
            <span className="block text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
              {t("results.questions_count", { count: attempt.questionCount })} · {attempt.timeLabel}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2 text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#5b616d]">
          <Clock3 aria-hidden="true" className="h-4 w-4" />
          {attempt.answerTimeSeconds == null ? "—" : t("results.per_answer", { time: `${Math.round(attempt.answerTimeSeconds)}s` })}
          {speed != null && (
            <span className={cn(
              "ml-1 inline-flex h-8 items-center rounded-full px-3",
              speed <= 0 ? "bg-[var(--dp-brand-soft)] text-[var(--dp-brand-button)]" : "bg-[#fdf6e7] text-[#a8791c]",
            )}>
              {speed <= 0
                ? t("results.faster", { seconds: Math.abs(speed) })
                : t("results.slower", { seconds: Math.abs(speed) })}
            </span>
          )}
        </span>
      </span>
      <span className="h-[88px] w-px shrink-0 bg-[#dddfe4]" />
      <span className="flex w-[82px] shrink-0 flex-col items-end gap-2">
        <ResultsTrendPill change={attempt.scoreChange} />
        <strong className="text-[36px] font-semibold leading-[42px] tracking-[-0.18px] text-[#a8791c]">
          {attempt.score}<span className="text-xs">%</span>
        </strong>
      </span>
    </button>
  );
}

export function ResultsEmptyState({
  buttonLabel,
  description,
  footer,
  onAction,
  title,
}: {
  buttonLabel: string;
  description: string;
  footer?: ReactNode;
  onAction: () => void;
  title: string;
}) {
  return (
    <section className="flex w-full max-w-[560px] flex-col items-center gap-6 rounded-[28px] border border-[#dddfe4] bg-white p-12 text-center shadow-[0_1px_1.5px_rgba(10,13,18,0.06),0_1px_1px_rgba(10,13,18,0.04)]">
      <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[var(--dp-brand-soft)] text-[var(--dp-brand-button)]">
        <BarChart3 aria-hidden="true" className="h-6 w-6" />
      </span>
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-2xl font-semibold leading-[31px] text-[#0a0c11]">{title}</h1>
        <p className="max-w-[420px] text-base leading-6 text-[#5b616d]">{description}</p>
      </div>
      <div className="flex flex-col items-center gap-3">
        <button className="dp-button-primary h-12 px-6 text-base" onClick={onAction} type="button">
          {buttonLabel}
        </button>
        {footer && <div className="text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">{footer}</div>}
      </div>
    </section>
  );
}
